import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A Line & Stacked Bar Chart combines two visualization types: a line chart and a stacked
          bar chart. The stacked bars represent the cumulative values of different categories over
          time, while the line chart displays trends or changes in a particular metric. This
          combination allows for a comprehensive view of both the total distribution and individual
          category trends. It&apos;s useful for analyzing how the components of a whole evolve over
          time and understanding their relative contributions.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">
          Combining the strengths of line and stacked bar charts, this hybrid visualization offers
          unique capabilities for multi-faceted data analysis. Here are three of its most
          significant uses:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Tracking Overall Trends and Category Contributions:</strong> These charts are
            perfect for analyzing overall trends (via the line graph) while simultaneously
            understanding how individual categories contribute to the total (through stacked bars).
          </li>
          <li>
            <strong>Visualizing Multi-Dimensional Data Over Time:</strong> They effectively present
            time-series data, showcasing both total values and the distribution among subcategories
            in a single chart.
          </li>
          <li>
            <strong>Comparing Proportions Across Time Periods:</strong> By blending stacked bars
            with a line, these charts enable clear comparisons of proportions within each time frame
            and between different periods.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Line & Stacked Bar Charts combine two different types of visualizations to show trends and
          categorical compositions. However, they also present certain limitations:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Difficulty in Comparing Trends and Proportions:</strong> The combination of line
            charts and stacked bars can make it hard to compare the overall trends alongside
            individual proportions. The line chart shows changes over time, but the stacked bars
            focus on the composition, which may distract from clear trend analysis.
          </li>
          <li>
            <strong>Cluttered Visuals with Multiple Categories:</strong> When too many categories
            are included, stacked bars can become cluttered, making it challenging to distinguish
            between them. This reduces the effectiveness of the chart, as individual category
            contributions become less apparent and harder to interpret.
          </li>
          <li>
            <strong>Misleading Representation of Small Trends:</strong> The stacked bar portion can
            obscure smaller trends in the data, especially if the top layers are large. These
            stacked elements can dominate the visual space, potentially hiding minor but significant
            variations in the underlying data that the line chart might have otherwise shown.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection in line and stacked bar charts is crucial for distinguishing multiple data
          series and ensuring readability. In line charts, different colors should be used for each
          line to avoid confusion, with bright or contrasting colors making it easier to track
          trends. For stacked bar charts, subtle gradients or distinct color bands can represent the
          various segments, making it clear how each part contributes to the whole. Using a
          consistent color scheme across both chart types can improve overall coherence, while
          ensuring accessibility by choosing colorblind-friendly palettes can enhance inclusivity.
          Thoughtful color choices enhance the viewer&apos;s ability to discern patterns and
          relationships in the data.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative', 'sequential']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Line and stacked bar charts combine two chart types for comparative insights, but they
          come with unique challenges:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Balancing Two Scales:</strong> Combining a line chart and a stacked bar chart
            often requires dual axes, which can confuse viewers if scales are not aligned or clearly
            labeled.
          </li>
          <li>
            <strong>Visual Clutter:</strong> The complexity of integrating two chart types can
            result in overlapping data points or crowded visuals, reducing the chart&apos;s clarity.
          </li>
          <li>
            <strong>Focus Distribution:</strong> The mix of line and stacked bars can split viewers’
            attention, making it harder to focus on specific trends or values in the data.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
